import { useEffect, useRef } from 'react';
import { useNavbarContent } from '../hooks/useNavbarContent';
import { validateCSS, validateJS } from '../utils/dynamicInjection';

const DynamicBodyClose = () => {
  const { data: navbarData } = useNavbarContent();
  const injectedRefs = useRef({
    bodyCloseCSS: null,
    bodyCloseJS: null,
  });

  useEffect(() => {
    if (!navbarData?.dataset?.data) return undefined;

    const data = navbarData.dataset.data;

    const injectCSS = (cssContent, id) => {
      if (!cssContent || cssContent.trim() === '') return null;

      const validation = validateCSS(cssContent);
      if (!validation.valid) return null;

      try {
        const existingStyle = document.getElementById(id);
        if (existingStyle) {
          existingStyle.remove();
        }

        const style = document.createElement('style');
        style.id = id;
        style.textContent = cssContent;
        document.head.appendChild(style);

        return style;
      } catch (error) {
        return null;
      }
    };

    const injectJS = (jsContent, id) => {
      if (!jsContent || jsContent.trim() === '') return null;

      const validation = validateJS(jsContent);
      if (!validation.valid) return null;

      try {
        const existingWrapper = document.getElementById(`${id}-wrapper`);
        if (existingWrapper) {
          existingWrapper.remove();
        }

        const wrapper = document.createElement('div');
        wrapper.id = `${id}-wrapper`;
        wrapper.style.display = 'none';

        const trimmed = jsContent.trim();
        if (trimmed.includes('<script')) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(trimmed, 'text/html');
          const scriptTags = doc.querySelectorAll('script');
          scriptTags.forEach((scriptTag) => {
            const script = document.createElement('script');

            if (scriptTag.hasAttribute('src')) {
              script.src = scriptTag.getAttribute('src');
              if (scriptTag.hasAttribute('async')) script.async = true;
              if (scriptTag.hasAttribute('defer')) script.defer = true;
            } else {
              script.textContent = scriptTag.textContent || '';
            }

            wrapper.appendChild(script);
          });
        } else {
          const script = document.createElement('script');
          script.id = id;
          script.textContent = jsContent;
          wrapper.appendChild(script);
        }

        document.body.appendChild(wrapper);

        return wrapper;
      } catch (error) {
        return null;
      }
    };

    if (data.body_close_custom_css) {
      injectedRefs.current.bodyCloseCSS = injectCSS(data.body_close_custom_css, 'dynamic-body-close-css');
    }

    if (data.body_close_custom_js) {
      injectedRefs.current.bodyCloseJS = injectJS(data.body_close_custom_js, 'dynamic-body-close-js');
    }

    return () => {
      Object.values(injectedRefs.current).forEach((ref) => {
        if (ref && ref.parentNode) {
          ref.remove();
        }
      });
    };
  }, [navbarData]);

  return null;
};

export default DynamicBodyClose;
